/**
 * Trovva — Automations Tab
 *
 * Broker/admin UI for managing automation rules:
 *   - List all automation rules with trigger/action summaries
 *   - Create / edit / delete rules via a modal editor
 *   - Toggle active/inactive
 *   - View execution log
 *   - Demo-mode fallback to canned data
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, Eye, EyeOff, AlertCircle,
  Zap, UserPlus, Tag, Mail, MessageSquare, Bell,
  FileText, CheckSquare, ArrowRight, Clock, Calendar,
  User, RefreshCw, Loader2, ChevronDown, ChevronUp,
  Play, Pause, Activity,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';

// ── Trigger definitions ──────────────────────────────
const TRIGGERS = [
  { value: 'lead_created',          label: 'New Lead Created',          icon: UserPlus,    color: theme.colors.info },
  { value: 'lead_status_changed',   label: 'Lead Status Changed',      icon: ArrowRight,  color: '#A855F7' },
  { value: 'lead_score_changed',    label: 'Lead Score Changed',       icon: Activity,    color: theme.colors.accent },
  { value: 'deal_created',          label: 'New Deal Created',         icon: FileText,    color: theme.colors.success },
  { value: 'deal_stage_changed',    label: 'Deal Stage Changed',       icon: ArrowRight,  color: '#06B6D4' },
  { value: 'contact_tag_added',     label: 'Tag Added to Contact',     icon: Tag,         color: theme.colors.warning },
  { value: 'contact_replied',       label: 'Contact Replied',          icon: MessageSquare, color: theme.colors.primary },
  { value: 'task_completed',        label: 'Task Completed',           icon: CheckSquare, color: theme.colors.success },
  { value: 'no_contact_days',       label: 'No Contact in X Days',     icon: Clock,       color: theme.colors.danger },
  { value: 'follow_up_overdue',     label: 'Follow-up Overdue',        icon: AlertCircle, color: theme.colors.danger },
  { value: 'deal_milestone_due',    label: 'Deal Milestone Due',       icon: Calendar,    color: theme.colors.warning },
  { value: 'enrollment_completed',  label: 'Action Plan Completed',    icon: CheckSquare, color: '#A855F7' },
];

// ── Action definitions ───────────────────────────────
const ACTION_TYPES = [
  { value: 'enroll_plan',      label: 'Enroll in Action Plan',   icon: Play },
  { value: 'assign_agent',     label: 'Assign Agent',            icon: User },
  { value: 'add_tags',         label: 'Add Tags',                icon: Tag },
  { value: 'remove_tags',      label: 'Remove Tags',             icon: Tag },
  { value: 'update_field',     label: 'Update Field',            icon: Edit2 },
  { value: 'create_task',      label: 'Create Task',             icon: CheckSquare },
  { value: 'send_notification', label: 'Send Notification',      icon: Bell },
  { value: 'send_email',       label: 'Send Email',              icon: Mail },
  { value: 'send_sms',         label: 'Send Text Message',       icon: MessageSquare },
  { value: 'stop_plan',        label: 'Stop Action Plan',        icon: Pause },
  { value: 'unenroll_plan',    label: 'Unenroll from Plan',      icon: Pause },
];

// Fields that can be updated by update_field action
const UPDATABLE_FIELDS = [
  { value: 'lead_status', label: 'Lead Status' },
  { value: 'lead_source', label: 'Lead Source' },
  { value: 'assigned_to', label: 'Assigned To' },
  { value: 'lead_type', label: 'Lead Type' },
];

// Conditions per trigger type
const CONDITION_FIELDS = {
  lead_status_changed: [{ key: 'new_status', label: 'New Status', type: 'text', placeholder: 'e.g. qualified' }],
  lead_score_changed: [{ key: 'min_score', label: 'Min Score', type: 'number', placeholder: '80' }],
  contact_tag_added: [{ key: 'tag', label: 'Tag Name', type: 'text', placeholder: 'e.g. vip' }],
  no_contact_days: [{ key: 'no_contact_days', label: 'Days Without Contact', type: 'number', placeholder: '14' }],
  follow_up_overdue: [{ key: 'overdue_days', label: 'Days Overdue', type: 'number', placeholder: '3' }],
  deal_stage_changed: [{ key: 'new_stage', label: 'New Stage', type: 'text', placeholder: 'e.g. under_contract' }],
  deal_milestone_due: [{ key: 'days_before', label: 'Days Before Due', type: 'number', placeholder: '3' }],
  lead_created: [{ key: 'lead_source', label: 'Lead Source', type: 'text', placeholder: 'e.g. zillow' }],
};

// ── Demo data ────────────────────────────────────────
const DEMO_RULES = [
  {
    id: '1', name: 'New Zillow Lead → Hot Buyer Drip', description: 'Auto-enroll Zillow buyer leads into the Hot Buyer action plan',
    trigger_event: 'lead_created', trigger_condition: { lead_source: 'zillow' },
    actions: [{ type: 'add_tags', tags: ['zillow', 'hot'] }, { type: 'enroll_plan', template_id: 'plan-1' }],
    is_active: true, run_once_per_contact: true, cooldown_hours: null, times_fired: 23, last_fired_at: new Date(Date.now() - 3600000).toISOString(),
    created_at: new Date(Date.now() - 86400000 * 7).toISOString(),
  },
  {
    id: '2', name: 'Stale Lead Alert (14 days)', description: 'Notify agent when a lead has no contact for 14 days',
    trigger_event: 'no_contact_days', trigger_condition: { no_contact_days: 14 },
    actions: [{ type: 'send_notification', message: '{{first_name}} {{last_name}} has had no contact in 14 days' }, { type: 'create_task', title: 'Follow up with {{first_name}}', priority: 'high', due_days: 1 }],
    is_active: true, run_once_per_contact: false, cooldown_hours: 336, times_fired: 8, last_fired_at: new Date(Date.now() - 7200000).toISOString(),
    created_at: new Date(Date.now() - 86400000 * 14).toISOString(),
  },
  {
    id: '3', name: 'Qualified → Under Contract Tag', description: null,
    trigger_event: 'lead_status_changed', trigger_condition: { new_status: 'under_contract' },
    actions: [{ type: 'add_tags', tags: ['under-contract'] }, { type: 'stop_plan', template_id: 'plan-1' }],
    is_active: false, run_once_per_contact: true, cooldown_hours: null, times_fired: 3, last_fired_at: new Date(Date.now() - 86400000 * 3).toISOString(),
    created_at: new Date(Date.now() - 86400000 * 5).toISOString(),
  },
];

const DEMO_LOG = [
  { id: 'l1', executed_at: new Date(Date.now() - 3600000).toISOString(), rule: { id: '1', name: 'New Zillow Lead → Hot Buyer Drip' }, contact: { id: 'c1', first_name: 'Marcus', last_name: 'Williams' }, actions_executed: 2, success: true },
  { id: 'l2', executed_at: new Date(Date.now() - 7200000).toISOString(), rule: { id: '2', name: 'Stale Lead Alert (14 days)' }, contact: { id: 'c2', first_name: 'Sarah', last_name: 'Chen' }, actions_executed: 2, success: true },
  { id: 'l3', executed_at: new Date(Date.now() - 86400000).toISOString(), rule: { id: '1', name: 'New Zillow Lead → Hot Buyer Drip' }, contact: { id: 'c3', first_name: 'David', last_name: 'Park' }, actions_executed: 2, success: true },
];


// ════════════════════════════════════════════════════════
// Action Row Editor (used inside RuleEditorModal)
// ════════════════════════════════════════════════════════
function ActionRowEditor({ action, index, onChange, onRemove }) {
  const actionDef = ACTION_TYPES.find(a => a.value === action.type);

  const updateAction = (field, value) => {
    onChange(index, { ...action, [field]: value });
  };

  return (
    <div style={{
      display: 'flex', gap: '8px', alignItems: 'flex-start',
      padding: '10px 12px', backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm,
    }}>
      <span style={{ marginTop: 4, color: theme.colors.textLight, fontSize: '12px', fontWeight: '600', minWidth: 20 }}>
        {index + 1}.
      </span>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <select
          value={action.type || ''}
          onChange={(e) => onChange(index, { type: e.target.value })}
          style={{
            padding: '6px 10px', border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.radius.sm, fontSize: '13px', boxSizing: 'border-box',
          }}
        >
          <option value="">Select action...</option>
          {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>

        {/* Conditional fields based on action type */}
        {action.type === 'add_tags' && (
          <input type="text" placeholder="Tags (comma-separated): vip, hot" value={action.tags?.join(', ') || ''}
            onChange={(e) => updateAction('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
            style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
        )}
        {action.type === 'remove_tags' && (
          <input type="text" placeholder="Tags to remove (comma-separated)" value={action.tags?.join(', ') || ''}
            onChange={(e) => updateAction('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
            style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
        )}
        {action.type === 'send_notification' && (
          <input type="text" placeholder="Message: New lead {{first_name}} {{last_name}}" value={action.message || ''}
            onChange={(e) => updateAction('message', e.target.value)}
            style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
        )}
        {action.type === 'create_task' && (
          <>
            <input type="text" placeholder="Task title: Call {{first_name}}" value={action.title || ''}
              onChange={(e) => updateAction('title', e.target.value)}
              style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <select value={action.priority || 'medium'} onChange={(e) => updateAction('priority', e.target.value)}
                style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', flex: 1 }}>
                <option value="low">Low priority</option>
                <option value="medium">Medium priority</option>
                <option value="high">High priority</option>
                <option value="urgent">Urgent</option>
              </select>
              <input type="number" placeholder="Due in days" value={action.due_days || ''} min="0"
                onChange={(e) => updateAction('due_days', parseInt(e.target.value, 10) || null)}
                style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', width: 100 }} />
            </div>
          </>
        )}
        {action.type === 'update_field' && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <select value={action.field || ''} onChange={(e) => updateAction('field', e.target.value)}
              style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', flex: 1 }}>
              <option value="">Select field...</option>
              {UPDATABLE_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <input type="text" placeholder="New value" value={action.value || ''}
              onChange={(e) => updateAction('value', e.target.value)}
              style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', flex: 1 }} />
          </div>
        )}
        {action.type === 'send_email' && (
          <>
            <input type="text" placeholder="Subject" value={action.subject || ''}
              onChange={(e) => updateAction('subject', e.target.value)}
              style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
            <textarea placeholder="Email body (supports {{first_name}}, {{agent_name}} merge fields)" value={action.body || ''} rows={3}
              onChange={(e) => updateAction('body', e.target.value)}
              style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
          </>
        )}
        {action.type === 'send_sms' && (
          <textarea placeholder="Text message body (supports merge fields)" value={action.body || ''} rows={2}
            onChange={(e) => updateAction('body', e.target.value)}
            style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
        )}
        {(action.type === 'enroll_plan' || action.type === 'stop_plan' || action.type === 'unenroll_plan') && (
          <input type="text" placeholder="Action plan template ID" value={action.template_id || ''}
            onChange={(e) => updateAction('template_id', e.target.value)}
            style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
        )}
        {action.type === 'assign_agent' && (
          <input type="text" placeholder="Agent ID (or leave blank for round-robin)" value={action.agent_id || ''}
            onChange={(e) => updateAction('agent_id', e.target.value)}
            style={{ padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
        )}
      </div>
      <button onClick={() => onRemove(index)}
        style={{ padding: '4px', backgroundColor: 'transparent', border: 'none', color: theme.colors.danger, cursor: 'pointer', marginTop: 4 }}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}


// ════════════════════════════════════════════════════════
// Rule Editor Modal
// ════════════════════════════════════════════════════════
function RuleEditorModal({ rule, onSave, onCancel }) {
  const [form, setForm] = useState(() => {
    if (rule) return { ...rule, trigger_condition: rule.trigger_condition || {} };
    return {
      name: '', description: '', trigger_event: 'lead_created',
      trigger_condition: {}, actions: [{ type: '' }],
      is_active: true, run_once_per_contact: false, cooldown_hours: null,
    };
  });

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleConditionChange = (key, value) => {
    setForm(prev => ({ ...prev, trigger_condition: { ...prev.trigger_condition, [key]: value } }));
  };

  const handleActionChange = (index, newAction) => {
    setForm(prev => {
      const actions = [...prev.actions];
      actions[index] = newAction;
      return { ...prev, actions };
    });
  };

  const addAction = () => setForm(prev => ({ ...prev, actions: [...prev.actions, { type: '' }] }));

  const removeAction = (index) => {
    setForm(prev => ({ ...prev, actions: prev.actions.filter((_, i) => i !== index) }));
  };

  const condFields = CONDITION_FIELDS[form.trigger_event] || [];

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onCancel}>
      <div style={{ backgroundColor: theme.colors.card, borderRadius: theme.radius.lg, padding: '24px', maxWidth: '640px', width: '92%', maxHeight: '90vh', overflowY: 'auto', boxShadow: theme.shadow.lg }}
        onClick={e => e.stopPropagation()}>

        <h2 style={{ margin: '0 0 20px 0', color: theme.colors.text, fontSize: '18px', fontWeight: '600' }}>
          {rule ? 'Edit Automation' : 'New Automation'}
        </h2>

        {/* Name */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: theme.colors.text }}>Name</label>
          <input type="text" value={form.name} onChange={e => handleChange('name', e.target.value)}
            placeholder="e.g. New Zillow Lead → Hot Buyer Drip"
            style={{ width: '100%', padding: '8px 12px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '14px', boxSizing: 'border-box' }} />
        </div>

        {/* Description */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: theme.colors.text }}>Description (optional)</label>
          <input type="text" value={form.description || ''} onChange={e => handleChange('description', e.target.value)}
            placeholder="What does this automation do?"
            style={{ width: '100%', padding: '8px 12px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '14px', boxSizing: 'border-box' }} />
        </div>

        {/* Trigger */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: theme.colors.text }}>When this happens...</label>
          <select value={form.trigger_event} onChange={e => { handleChange('trigger_event', e.target.value); handleChange('trigger_condition', {}); }}
            style={{ width: '100%', padding: '8px 12px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '14px', boxSizing: 'border-box' }}>
            {TRIGGERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {/* Trigger conditions */}
        {condFields.length > 0 && (
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Conditions
            </label>
            {condFields.map(cf => (
              <div key={cf.key} style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: theme.colors.text }}>{cf.label}</label>
                <input type={cf.type} placeholder={cf.placeholder}
                  value={form.trigger_condition[cf.key] || ''}
                  onChange={e => handleConditionChange(cf.key, cf.type === 'number' ? parseInt(e.target.value, 10) || '' : e.target.value)}
                  style={{ width: '100%', padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: theme.colors.text }}>
            ...then do this
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {form.actions.map((action, i) => (
              <ActionRowEditor key={i} action={action} index={i}
                onChange={handleActionChange} onRemove={removeAction} />
            ))}
          </div>
          <button onClick={addAction} style={{
            marginTop: '8px', padding: '6px 14px', backgroundColor: 'transparent',
            border: `1px dashed ${theme.colors.border}`, borderRadius: theme.radius.sm,
            color: theme.colors.primary, fontSize: '13px', fontWeight: '500', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <Plus size={14} /> Add action
          </button>
        </div>

        {/* Settings */}
        <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: theme.colors.bg, borderRadius: theme.radius.sm }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '10px', color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Settings
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
              <input type="checkbox" checked={form.run_once_per_contact} onChange={e => handleChange('run_once_per_contact', e.target.checked)}
                style={{ marginRight: '8px', cursor: 'pointer' }} />
              Run only once per contact
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '13px', color: theme.colors.text }}>Cooldown:</label>
              <input type="number" value={form.cooldown_hours || ''} placeholder="None" min="1"
                onChange={e => handleChange('cooldown_hours', parseInt(e.target.value, 10) || null)}
                style={{ width: 80, padding: '6px 10px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, fontSize: '13px' }} />
              <span style={{ fontSize: '13px', color: theme.colors.textLight }}>hours between fires for same contact</span>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{
            padding: '8px 16px', border: `1px solid ${theme.colors.border}`,
            backgroundColor: theme.colors.card, color: theme.colors.text,
            borderRadius: theme.radius.sm, fontSize: '14px', fontWeight: '500', cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={() => {
            const payload = { ...form };
            // Strip empty actions
            payload.actions = payload.actions.filter(a => a.type);
            onSave(payload);
          }} style={{
            padding: '8px 16px', backgroundColor: theme.colors.primary, color: 'white',
            border: 'none', borderRadius: theme.radius.sm, fontSize: '14px', fontWeight: '500', cursor: 'pointer',
          }}>
            {rule ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════
// Main Component
// ════════════════════════════════════════════════════════
export default function AutomationsTab({ demoMode, showToast }) {
  const [rules, setRules] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [loadingLog, setLoadingLog] = useState(false);

  // ── Load rules ────────────────────────────────────
  const loadRules = useCallback(async () => {
    if (demoMode) { setRules(DEMO_RULES); setLoading(false); return; }
    try {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const res = await fetch('/api/automations', {
        headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
      });
      const data = await res.json();
      setRules(data.rules || []);
    } catch (err) {
      console.error('Error loading automations:', err);
      showToast?.('Failed to load automations', 'error');
    } finally { setLoading(false); }
  }, [demoMode, showToast]);

  // ── Load execution log ────────────────────────────
  const loadLog = useCallback(async () => {
    if (demoMode) { setLog(DEMO_LOG); setLoadingLog(false); return; }
    try {
      setLoadingLog(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const res = await fetch('/api/automations?log=1&limit=50', {
        headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
      });
      const data = await res.json();
      setLog(data.log || []);
    } catch (err) {
      console.error('Error loading log:', err);
      showToast?.('Failed to load log', 'error');
    } finally { setLoadingLog(false); }
  }, [demoMode, showToast]);

  useEffect(() => { loadRules(); }, []);

  // ── Save rule ────────────────────────────────────
  const handleSaveRule = async (payload) => {
    if (!payload.name?.trim()) { showToast?.('Name is required', 'error'); return; }
    if (!payload.actions?.length) { showToast?.('Add at least one action', 'error'); return; }

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const method = editingRule ? 'PATCH' : 'POST';
      const url = editingRule ? `/api/automations?id=${editingRule.id}` : '/api/automations';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionData.session.access_token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to save');

      showToast?.(editingRule ? 'Automation updated' : 'Automation created', 'success');
      setShowEditor(false);
      setEditingRule(null);
      loadRules();
    } catch (err) {
      console.error('Error saving automation:', err);
      showToast?.('Failed to save automation', 'error');
    }
  };

  // ── Delete rule ──────────────────────────────────
  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this automation?')) return;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const res = await fetch(`/api/automations?id=${ruleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      showToast?.('Automation deleted', 'success');
      loadRules();
    } catch (err) {
      console.error('Error deleting:', err);
      showToast?.('Failed to delete', 'error');
    }
  };

  // ── Toggle active ────────────────────────────────
  const handleToggle = async (ruleId, isActive) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      await fetch(`/api/automations?id=${ruleId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionData.session.access_token}` },
        body: JSON.stringify({ is_active: !isActive }),
      });
      loadRules();
    } catch (err) {
      console.error('Error toggling:', err);
      showToast?.('Failed to toggle', 'error');
    }
  };

  // ── Helpers ──────────────────────────────────────
  const getTriggerInfo = (event) => TRIGGERS.find(t => t.value === event);
  const getActionLabel = (type) => ACTION_TYPES.find(a => a.value === type)?.label || type;

  const formatActionSummary = (actions) => {
    if (!actions?.length) return 'No actions';
    return actions.map(a => getActionLabel(a.type)).join(' → ');
  };

  const formatConditionSummary = (trigger, cond) => {
    if (!cond || !Object.keys(cond).length) return '';
    const parts = Object.entries(cond).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`);
    return parts.join(', ');
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return 'Never';
    const ms = Date.now() - new Date(dateStr).getTime();
    if (ms < 3600000) return `${Math.round(ms / 60000)}m ago`;
    if (ms < 86400000) return `${Math.round(ms / 3600000)}h ago`;
    return `${Math.round(ms / 86400000)}d ago`;
  };

  // ── Loading state ────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: theme.colors.textLight }}>
        Loading automations...
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0, color: theme.colors.text, fontSize: '20px', fontWeight: '600' }}>
          Automations
        </h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => { setShowLog(!showLog); if (!showLog) loadLog(); }}
            style={{
              padding: '8px 16px',
              backgroundColor: showLog ? theme.colors.primaryLight : theme.colors.bg,
              color: showLog ? 'white' : theme.colors.text,
              border: 'none', borderRadius: theme.radius.sm,
              fontSize: '14px', fontWeight: '500', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
            {showLog ? <EyeOff size={16} /> : <Eye size={16} />}
            {showLog ? 'Hide Log' : 'Execution Log'}
          </button>
          <button onClick={() => { setEditingRule(null); setShowEditor(true); }}
            style={{
              padding: '8px 16px', backgroundColor: theme.colors.primary, color: 'white',
              border: 'none', borderRadius: theme.radius.sm,
              fontSize: '14px', fontWeight: '500', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
            <Plus size={16} /> New Automation
          </button>
        </div>
      </div>

      {/* ── Rules List ── */}
      {!showLog ? (
        rules.length === 0 ? (
          <div style={{
            backgroundColor: theme.colors.card, border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.radius.md, padding: '40px 24px', textAlign: 'center',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
          }}>
            <Zap size={32} color={theme.colors.textLight} />
            <p style={{ color: theme.colors.textLight, fontSize: '14px', margin: 0 }}>
              No automations yet. Create your first rule to automate lead management tasks.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {rules.map(rule => {
              const triggerInfo = getTriggerInfo(rule.trigger_event);
              const TriggerIcon = triggerInfo?.icon || Zap;
              const condStr = formatConditionSummary(rule.trigger_event, rule.trigger_condition);

              return (
                <div key={rule.id} style={{
                  backgroundColor: theme.colors.card, border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.radius.md, padding: '16px',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                  opacity: rule.is_active ? 1 : 0.65,
                  transition: 'box-shadow 0.2s',
                }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = theme.shadow.md}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>

                  <div style={{ flex: 1 }}>
                    {/* Title row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <h3 style={{ margin: 0, color: theme.colors.text, fontSize: '15px', fontWeight: '600' }}>{rule.name}</h3>
                      {!rule.is_active && (
                        <span style={{ padding: '2px 8px', backgroundColor: '#FEE2E2', color: theme.colors.danger, borderRadius: theme.radius.sm, fontSize: '11px', fontWeight: '600' }}>
                          PAUSED
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    {rule.description && (
                      <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: theme.colors.textLight }}>{rule.description}</p>
                    )}

                    {/* Trigger badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '3px 8px', backgroundColor: triggerInfo?.color || theme.colors.bg,
                        color: 'white', borderRadius: theme.radius.sm, fontSize: '11px', fontWeight: '500',
                      }}>
                        <TriggerIcon size={12} />
                        {triggerInfo?.label}
                      </span>
                      {condStr && (
                        <span style={{ fontSize: '12px', color: theme.colors.textLight }}>
                          {condStr}
                        </span>
                      )}
                    </div>

                    {/* Actions summary */}
                    <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: theme.colors.textLight }}>
                      <strong>Actions:</strong> {formatActionSummary(rule.actions)}
                    </p>

                    {/* Stats */}
                    <p style={{ margin: 0, fontSize: '12px', color: theme.colors.textLight }}>
                      Fired {rule.times_fired || 0} time{rule.times_fired !== 1 ? 's' : ''}
                      {rule.last_fired_at && ` · Last: ${timeAgo(rule.last_fired_at)}`}
                      {rule.run_once_per_contact && ' · Once per contact'}
                      {rule.cooldown_hours && ` · ${rule.cooldown_hours}h cooldown`}
                    </p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '12px' }}>
                    <button onClick={() => handleToggle(rule.id, rule.is_active)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: rule.is_active ? theme.colors.success : theme.colors.bg,
                        color: rule.is_active ? 'white' : theme.colors.textLight,
                        border: 'none', borderRadius: theme.radius.sm,
                        fontSize: '12px', fontWeight: '500', cursor: 'pointer',
                      }}>
                      {rule.is_active ? 'Active' : 'Paused'}
                    </button>
                    <button onClick={() => { setEditingRule(rule); setShowEditor(true); }}
                      style={{
                        padding: '6px 10px', backgroundColor: 'transparent',
                        color: theme.colors.primary, border: `1px solid ${theme.colors.border}`,
                        borderRadius: theme.radius.sm, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '500',
                      }}>
                      <Edit2 size={14} /> Edit
                    </button>
                    <button onClick={() => handleDelete(rule.id)}
                      style={{
                        padding: '6px 10px', backgroundColor: 'transparent',
                        color: theme.colors.danger, border: `1px solid ${theme.colors.border}`,
                        borderRadius: theme.radius.sm, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '500',
                      }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* ── Execution Log ── */
        <div style={{
          backgroundColor: theme.colors.card, border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.radius.md, overflow: 'hidden',
        }}>
          {loadingLog ? (
            <div style={{ padding: '24px', textAlign: 'center', color: theme.colors.textLight }}>Loading log...</div>
          ) : log.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: theme.colors.textLight }}>No execution history yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: theme.colors.bg, borderBottom: `1px solid ${theme.colors.border}` }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.text }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.text }}>Automation</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.text }}>Contact</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.text }}>Actions</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.text }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {log.map(entry => (
                  <tr key={entry.id} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                    <td style={{ padding: '12px', color: theme.colors.textLight }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={14} />
                        {new Date(entry.executed_at).toLocaleString()}
                      </div>
                    </td>
                    <td style={{ padding: '12px', color: theme.colors.text, fontWeight: '500' }}>{entry.rule?.name || '—'}</td>
                    <td style={{ padding: '12px', color: theme.colors.text }}>{entry.contact?.first_name} {entry.contact?.last_name}</td>
                    <td style={{ padding: '12px', color: theme.colors.textLight }}>{entry.actions_executed || 0} executed</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: theme.radius.sm, fontSize: '11px', fontWeight: '600',
                        backgroundColor: entry.success ? '#D1FAE5' : '#FEE2E2',
                        color: entry.success ? theme.colors.success : theme.colors.danger,
                      }}>
                        {entry.success ? 'OK' : 'FAILED'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && (
        <RuleEditorModal
          rule={editingRule}
          onSave={handleSaveRule}
          onCancel={() => { setShowEditor(false); setEditingRule(null); }}
        />
      )}
    </div>
  );
}
